import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RowSelectionState } from "@tanstack/react-table";
import type {
  ProductGroupId,
  ProductId,
  ProductVariantId,
  StoreId,
} from "@KassaroAB/shared/types/ids";
import { CatalogOrigin } from "@KassaroAB/shared/types/enums/CatalogOrigin";
import type {
  RestBulkUpdateProductsResponse,
  RestProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";
import type {
  ProductOverlayField,
  ProductVariantOverlayField,
  RestProductOverlayListItem,
  RestUpsertProductOverlay,
} from "@KassaroAB/shared/types/productOverlay";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import { bulkUpdateProductsApi } from "../../../api/productsApi";
import type { CatalogScope } from "../../../api/catalogScope";
import {
  productOverlaysQueryKey,
  useBulkUpsertProductOverlaysMutation,
} from "../../../query/productOverlaysQuery";
import {
  omitUnchangedVariantStock,
  pruneUnchangedProductEdits,
  toOverlayUpdate,
} from "../../../utils/productEditDiff";
import {
  getEffectiveProductGroupId,
  stripGroupLockedEdits,
} from "../../../utils/productGroupLock";

interface UseProductTableEditsParams {
  scope: CatalogScope | null;
  rows: RestProduct[];
  selectedIds: ProductId[];
  productGroupsById: ReadonlyMap<ProductGroupId, RestProductGroup>;
  applyUpdated: (products: RestProduct[]) => Promise<void>;
  setRowSelection: (value: RowSelectionState) => void;
  overlayStoreId: StoreId | null;
  saveChainToOverlay: boolean;
  overlays: ReadonlyMap<ProductId, RestProductOverlayListItem>;
  restrictChanges: (
    product: RestProduct,
    changes: RestUpdateProduct,
  ) => RestUpdateProduct;
}

const failAll = (productIds: ProductId[], error: unknown) => ({
  updated: [],
  failed: productIds.map((productId) => ({
    productId,
    error: error instanceof Error ? error.message : "Unknown error",
  })),
});

export const useProductTableEdits = ({
  scope,
  rows,
  selectedIds,
  productGroupsById,
  applyUpdated,
  setRowSelection,
  overlayStoreId,
  saveChainToOverlay,
  overlays,
  restrictChanges,
}: UseProductTableEditsParams) => {
  const queryClient = useQueryClient();
  const { mutateAsync: upsertOverlays, isPending: isResetting } =
    useBulkUpsertProductOverlaysMutation(overlayStoreId);
  const [edits, setEdits] = useState<Record<string, RestUpdateProduct>>({});
  const editedProductsRef = useRef<Record<string, RestProduct>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] =
    useState<RestBulkUpdateProductsResponse | null>(null);

  const setEdit = useCallback(
    (id: ProductId, product: RestProduct, changes: RestUpdateProduct) => {
      editedProductsRef.current[id] ??= product;
      setEdits((previous) => {
        const merged = restrictChanges(product, { ...previous[id], ...changes });
        const groupId = getEffectiveProductGroupId(product, merged);
        const group = groupId ? productGroupsById.get(groupId) : undefined;
        const pruned = stripGroupLockedEdits(
          pruneUnchangedProductEdits(product, merged),
          group,
        );
        if (Object.keys(pruned).length === 0) {
          delete editedProductsRef.current[id];
          if (!(id in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[id];
          return next;
        }
        return { ...previous, [id]: pruned };
      });
    },
    [productGroupsById, restrictChanges],
  );

  const applyToSelected = useCallback(
    (changes: RestUpdateProduct) => {
      setEdits((previous) => {
        const next = { ...previous };
        selectedIds.forEach((id) => {
          const product = rows.find((row) => row.id === id);
          if (!product) {
            next[id] = { ...next[id], ...changes };
            return;
          }
          const merged = restrictChanges(product, { ...next[id], ...changes });
          const groupId = getEffectiveProductGroupId(product, merged);
          const group = groupId ? productGroupsById.get(groupId) : undefined;
          const pruned = stripGroupLockedEdits(
            pruneUnchangedProductEdits(product, merged),
            group,
          );
          if (Object.keys(pruned).length === 0) {
            delete next[id];
          } else {
            next[id] = pruned;
          }
        });
        return next;
      });
    },
    [selectedIds, rows, productGroupsById, restrictChanges],
  );

  const clearEdits = useCallback(() => {
    editedProductsRef.current = {};
    setEdits({});
  }, []);

  const editCount = Object.keys(edits).length;

  const save = useCallback(async () => {
    if (!scope || editCount === 0) {
      return;
    }
    const entries = Object.entries(edits).map(([productId, changes]) => ({
      productId: productId as ProductId,
      product:
        editedProductsRef.current[productId] ??
        rows.find((row) => row.id === productId),
      changes,
    }));
    const isOverlayEntry = (entry: (typeof entries)[number]) =>
      saveChainToOverlay && entry.product?.origin === CatalogOrigin.CHAIN;
    const updates = entries
      .filter((entry) => !isOverlayEntry(entry))
      .map(({ productId, changes }) => ({
        productId,
        changes: changes.variants
          ? {
              ...changes,
              variants: omitUnchangedVariantStock(
                editedProductsRef.current[productId]?.variants,
                changes.variants,
              ),
            }
          : changes,
      }));
    const overlayUpdates = entries
      .filter(isOverlayEntry)
      .map(({ productId, product, changes }) => ({
        productId,
        overlay: toOverlayUpdate(product!, changes),
      }));
    const empty: RestBulkUpdateProductsResponse = { updated: [], failed: [] };
    setIsSaving(true);
    setSaveResult(null);
    await Promise.all([
      updates.length > 0
        ? bulkUpdateProductsApi(scope, updates).catch((error) =>
            failAll(
              updates.map((update) => update.productId),
              error,
            ),
          )
        : empty,
      overlayUpdates.length > 0
        ? upsertOverlays(overlayUpdates).catch((error) =>
            failAll(
              overlayUpdates.map((update) => update.productId),
              error,
            ),
          )
        : empty,
    ])
      .then(async ([local, overlay]) => {
        const result: RestBulkUpdateProductsResponse = {
          updated: [...local.updated, ...overlay.updated],
          failed: [...local.failed, ...overlay.failed],
        };
        setSaveResult(result);
        await applyUpdated(result.updated);
        const failedIds = new Set(
          result.failed.map((failure) => failure.productId),
        );
        result.updated.forEach((product) => {
          delete editedProductsRef.current[product.id];
        });
        setEdits((previous) => {
          const next: Record<string, RestUpdateProduct> = {};
          Object.entries(previous).forEach(([id, changes]) => {
            if (failedIds.has(id as ProductId)) {
              next[id] = changes;
            }
          });
          return next;
        });
        setRowSelection({});
      })
      .catch((error) => {
        setSaveResult(
          failAll(
            entries.map((entry) => entry.productId),
            error,
          ),
        );
      });
    if (
      overlayStoreId &&
      updates.some((update) => overlays.has(update.productId))
    ) {
      void queryClient.invalidateQueries({
        queryKey: productOverlaysQueryKey(overlayStoreId),
      });
    }
    setIsSaving(false);
  }, [
    scope,
    edits,
    editCount,
    rows,
    saveChainToOverlay,
    upsertOverlays,
    overlayStoreId,
    overlays,
    queryClient,
    applyUpdated,
    setRowSelection,
  ]);

  const resetOverride = useCallback(
    async (
      productId: ProductId,
      field: ProductOverlayField,
      variantId?: ProductVariantId,
    ) => {
      const overlay: RestUpsertProductOverlay = variantId
        ? {
            resetVariantFields: {
              [variantId]: [field as ProductVariantOverlayField],
            },
          }
        : { resetFields: [field] };
      setSaveResult(null);
      await upsertOverlays([{ productId, overlay }])
        .then(async (result) => {
          if (result.failed.length > 0) {
            setSaveResult({ updated: [], failed: result.failed });
          }
          await applyUpdated(result.updated);
        })
        .catch((error) => {
          setSaveResult(failAll([productId], error));
        });
    },
    [upsertOverlays, applyUpdated],
  );

  return {
    edits,
    setEdit,
    applyToSelected,
    clearEdits,
    editCount,
    save,
    isSaving,
    saveResult,
    resetOverride,
    isResetting,
  };
};
